import type { ListBuilderList } from '../../../types';
import {
	createClientActionsColumn,
	createClientNameColumn,
	createContactInfoColumn,
	createLastStorefrontActivityColumn,
	createLocationColumn,
	createReorderColumn,
	createSalesTrendColumn,
	createSalespeopleColumn,
	createStorefrontVisits30dColumn,
} from '../columns';
import {
	CLIENT_MOBILE_CARD_CONFIG,
	DEFAULT_LIST_REQUIRED_FIELDS,
	SALESPEOPLE_FILTER_RULES,
	createClientKanbanConfig,
} from '../shared';

/**
 * PD-17191 — Recently Online: accounts whose users were on a storefront in
 * the last 7 days, most recent first, so a rep can follow up while the
 * interest is fresh. The storefront dashboard widget's "View all" opens it
 * (`/clients?list=recently-online-list`).
 *
 * Gated on the derived `storefront` feature (the tenant has an active
 * storefront site), so a tenant without one never gets an always-empty tab;
 * ListConfigService drops it server-side. The window is a locked, hidden
 * Last Storefront Activity filter rather than a bespoke query, so the tab
 * count and the rows come from the same client list path as every other tab.
 */
export const recentlyOnlineList: ListBuilderList = {
	id: 'recently-online-list',
	cacheTTL: 600000,
	name: 'Recently Online',
	type: 'default',
	active: true,
	bulkActionTemplate: 'clients',
	// Right after Dormant Customers (4).
	sortOrder: 5,
	requiredFeature: 'storefront',
	filterRules: {
		// The server derives a user's client visibility from the requested
		// list's salespeople rule (PD-15546); without it a `view:self` user
		// could read the whole book through this listId.
		...SALESPEOPLE_FILTER_RULES,
		lastStorefrontActivity: {
			defaultValue: [{ id: '7d', label: 'Last 7 Days' }],
			locked: true,
			hidden: true,
		},
	},
	columns: [
		createClientNameColumn(),
		createLastStorefrontActivityColumn(),
		createStorefrontVisits30dColumn(),
		createLocationColumn(),
		createSalespeopleColumn({ includeStage: true }),
		createContactInfoColumn(),
		createReorderColumn(),
		createSalesTrendColumn(),
		createClientActionsColumn(),
	],
	mobileCardConfig: CLIENT_MOBILE_CARD_CONFIG,
	description: 'Accounts whose users were on a storefront in the last 7 days',
	defaultSortId: 'last-storefront-activity-desc',
	// An authored requiredFields wins over extraction from the columns, so
	// the two storefront fields are named here or they are never requested.
	requiredFields: [
		...DEFAULT_LIST_REQUIRED_FIELDS,
		'client.lastStorefrontActivityDate',
		'client.storefrontVisits30d',
	],
	kanbanConfig: createClientKanbanConfig(),
};
